import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import "./RecommendedSearchBox.css";
import DynamicTextField from "../Textfield/Textfield";
import Button from "../Button/Button";
import jellyIconLight from "../../Assets/Images/jelly-icon-light.svg";
import jellyIconDark from "../../Assets/Images/jelly-icon-dark.svg";
import dropRoundLight from "../../Assets/Images/drop-down-round-light.svg";
import dropRoundDark from "../../Assets/Images/drop-down-round-dark.svg";
import FileIcon from "../../Common/Common-Function/IconMapping";
import { displayFileName } from "../../Common/Common-Function/CommonFunctions";
import CancelIcon from "@mui/icons-material/Cancel";
import TextArea from "../../Common/TextArea/TextArea";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import Badge from "@mui/material/Badge";
import ChatPopup from "../../Common/VybogTal-Chat-Popup/ChatPopup";
import infoLightIcon from "../../Assets/Images/VybogTAL-Chat-info-icon.svg";
import infoDarkIcon from "../../Assets/Images/info-icon-Dark.svg";
import simpleSearchNotIcon from "../../Assets/Images/search-info-outline-rounded.svg";
const RecommendedSearchBox = ({
  jobDescriptionValue = "",
  askMeAnythingValue = "",
  jobDescriptionOnChange,
  onAskChange,
  onSubmit,
  uploadedFiles = [],
  onFileDelete,
  askMeAnythingPlaceholder = "Search",
  jobDescriptionPlaceholder = "Enter any details about the job you're looking to fill.",
  subText,
  isDarkMode,
  buttonLabel = "Search",
  inputsectionstyle,
  inputsectionstylewrap,
  TextAreaStyle,
  isCollapsed = false,
  onCollapseChange,
  onClearForm,
  showJobDescriptionField = true,
  showJobDescalone = false,
  showAttachResume = true,
  submitErrorMessage = "Please enter a search query.",
  recommendedSearchBoxTitle = "Recommend Candidate Search",
  askmeanythingOnKeyDown = () => {},
  headerRightContent = null,
  inlineActionsRight = null,
  inlineActionsLeft = null,
  isSearchLoading = false,
  showBadge = false,
  isRecommendedPage = false,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    const hasFiles = Array.isArray(uploadedFiles) && uploadedFiles.length > 0;
    const hasJobDescription = jobDescriptionValue?.trim();
    const hasAskMeAnything = askMeAnythingValue?.trim();
    if (hasFiles || hasJobDescription || hasAskMeAnything) {
      onSubmit();
    } else {
      Toast("error", submitErrorMessage);
    }
  };

  const [collapsed, setCollapsed] = useState(false);
  const fileInputRef = useRef(null);
  const askMeAnythingRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);

  const handleChatPopupClose = useCallback(
    (selectedSuggestion) => {
      if (selectedSuggestion) {
        onAskChange({
          target: { value: selectedSuggestion, name: "askmeanything" },
        });
      }
      setIsChatPopupOpen(false);
    },
    [onAskChange],
  );

  const getLineCount = (text) => {
    if (!text) return 1;
    return text.split("\n").length;
  };

  useEffect(() => {
    if (isCollapsed !== collapsed) {
      setCollapsed(isCollapsed);
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (onClearForm && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [onClearForm]);

  useLayoutEffect(() => {
    const el = askMeAnythingRef.current;
    if (!el) return;

    const lineCount = getLineCount(askMeAnythingValue);
    const isCleared =
      !askMeAnythingValue || askMeAnythingValue.trim().length === 0;

    const previousHeight = el.style.height;
    try {
      el.style.height = "30px";
      const hasSoftWrap = el.scrollHeight > el.clientHeight + 1;

      let nextExpanded = isExpanded;
      if (isCleared && lineCount > 1) {
        nextExpanded = true;
      } else if (lineCount > 1 || hasSoftWrap) {
        nextExpanded = true;
      } else if (isCleared || (lineCount === 1 && !hasSoftWrap)) {
        nextExpanded = false;
      }

      if (nextExpanded !== isExpanded) {
        setIsExpanded(nextExpanded);
      }
    } finally {
      el.style.height = previousHeight;
    }
  }, [askMeAnythingValue]);

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) return `${sizeInBytes} bytes`;
    else if (sizeInBytes < 1048576)
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    else return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
  };

  const toggleCollapse = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  };

  const renderedInlineActions =
    typeof inlineActionsRight === "function"
      ? inlineActionsRight({ isExpanded })
      : inlineActionsRight;

  const howItWorksBadge = (
    <Badge
      badgeContent={
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <img
            src={isDarkMode ? infoDarkIcon : infoLightIcon}
            alt="info"
            style={{ width: "14px", height: "14px" }}
          />
          <span>How it Works</span>
        </div>
      }
      onClick={() => setIsChatPopupOpen(true)}
      sx={{
        cursor: "pointer",
        "& .MuiBadge-badge": {
          cursor: "pointer",
          fontSize: "10px",
          height: "24px",
          marginTop: "2px",
          minWidth: "32px",
          padding: "2px 8px",
          backgroundColor: isDarkMode
            ? "rgb(176 172 255 / 10%)"
            : "var(--skill-badge-bg)",
          color: isDarkMode ? "rgb(176 172 255)" : "#5c47ce",
          border: isDarkMode
            ? "1px solid rgb(176 172 255 / 40%)"
            : "1px solid #5c47ce",
          position: "static",
          transform: "none",
          borderRadius: "12px",
        },
      }}
    />
  );

  return (
    <div className="recommended-search-container">
      <div
        className={`recommended-search-header${collapsed ? " collapsed" : ""}`}
        style={{
          borderBottomLeftRadius: collapsed ? "16px" : "0px",
          borderBottomRightRadius: collapsed ? "16px" : "0px",
        }}
      >
        {/* <img src={isDarkMode ? jellyIconDark : jellyIconLight} alt="Jelly Icon" className="jelly-icon" /> */}
        <span
          className="recommended-search-title"
          style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
        >
          {recommendedSearchBoxTitle}
          <Badge
            badgeContent="BETA"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                height: "20px",
                marginTop: "2px",
                minWidth: "32px",
                padding: "2px 8px",
                backgroundColor: "#fff4e5",
                color: "#ffa408",
                border: "1px solid #ffa408",
                position: "static",
                transform: "none",
                borderRadius: "12px",
              },
            }}
          />
          {/* {!isRecommendedPage && howItWorksBadge} */}
        </span>
        <span className="recommended-search-subtext">{subText}</span>
        {headerRightContent && (
          <div className="recommended-header-actions">{headerRightContent}</div>
        )}
        <span
          style={{
            fontSize: "12px",
            backgroundColor: "#fff4e5",
            color: "#ffa408",
            border: "1px solid #ffa408",
            padding: "2px 8px",
            borderRadius: "12px",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            whiteSpace: "nowrap",
          }}
        >
          <img src={simpleSearchNotIcon} alt="info" />
          This feature is in beta - responses are still evolving
        </span>
        <img
          src={isDarkMode ? dropRoundDark : dropRoundLight}
          style={{
            transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.25s ease",
          }}
          alt="Toggle"
          className={`dropdown-icon toggle-icon ${
            collapsed ? "collapsed" : ""
          }`}
          onClick={toggleCollapse}
        />
      </div>

      {!collapsed && (
        <>
          <div
            className="rec-search-input-section-wrap"
            style={{ ...inputsectionstylewrap }}
          >
            {showJobDescalone ? (
              <div
                className="rec-search-input-section"
                style={{ ...inputsectionstyle }}
              >
                <DynamicTextField
                  style={{
                    paddingLeft: "20px",
                    width: "100%",
                    border: "1px solid var(--search-input-section-border)",
                    borderRight: "none",
                    borderRadius: "10px 0px 0px 10px",
                  }}
                  containerStyle={{ flexGrow: 1, width: "auto" }}
                  placeholder={jobDescriptionPlaceholder}
                  className="ai-search-textfield"
                  name="jobDescription"
                  value={jobDescriptionValue}
                  onChange={jobDescriptionOnChange}
                />

                <Button
                  label={buttonLabel}
                  event={handleSubmit}
                  backgroundColor="var(--search-button-background-color)"
                  color="#ffffff"
                  iconWidth="20px"
                  className="search-button-sharp-left"
                  superShine={true}
                  style={{
                    borderTopLeftRadius: "0",
                    borderBottomLeftRadius: "0",
                    borderTopRightRadius: "10px",
                    borderBottomRightRadius: "10px",
                    backgroundColor: "var(--search-button-background-color)",
                    color: "#ffffff",
                    padding: "10px 10px",
                  }}
                />
              </div>
            ) : (
              <>
                {showJobDescriptionField && (
                  <TextArea
                    placeholder={jobDescriptionPlaceholder}
                    className="ai-search-textfield"
                    name="jobDescription"
                    value={jobDescriptionValue}
                    onChange={jobDescriptionOnChange}
                    style={{ ...TextAreaStyle }}
                  />
                )}
                <div
                  className="rec-search-input-section"
                  style={{ ...inputsectionstyle }}
                >
                  {inlineActionsLeft && (
                    <div className="rec-inline-actions rec-inline-actions-left">
                      {typeof inlineActionsLeft === "function"
                        ? inlineActionsLeft({ isExpanded })
                        : inlineActionsLeft}
                    </div>
                  )}
                  <div style={{ position: "relative", flexGrow: 1, display: "flex", alignItems: "center" }}>
                    <textarea
                      ref={askMeAnythingRef}
                      value={askMeAnythingValue}
                      onChange={onAskChange}
                      name="askmeanything"
                      placeholder={askMeAnythingPlaceholder}
                      disabled={isSearchLoading}
                      style={{
                        paddingLeft: "20px",
                        paddingRight: isRecommendedPage ? "115px" : "10px",
                        width: "100%",
                        border: "1px solid var(--search-input-section-border)",
                        borderRight: "none",
                        borderRadius: "10px 0px 0px 10px",
                        backgroundColor: "var(--body__background)",
                        height: isExpanded ? "70px" : "30px",
                        color: "var(--header__color)",
                        paddingTop: "9px",
                        overflowY: isExpanded ? "auto" : "hidden",
                        flexGrow: 1,
                        opacity: isSearchLoading ? 0.6 : 1,
                        cursor: isSearchLoading ? "not-allowed" : "text",
                      }}
                      onKeyDown={askmeanythingOnKeyDown}
                    />
                    {/* {isRecommendedPage && (
                      <div style={{ 
                        position: "absolute", 
                        right: "12px", 
                        top: isExpanded ? "12px" : "50%", 
                        transform: isExpanded ? "none" : "translateY(-50%)", 
                        zIndex: 10 
                      }}>
                        {howItWorksBadge}
                      </div>
                    )} */}
                  </div>

                  <Button
                    label={
                  <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        {buttonLabel}
                        {showBadge && (
                          <Badge
                            badgeContent="BETA"
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "10px",
                                backgroundColor: "#e38608",
                                color: "#ffff",
                                position: "static",
                                transform: "none",
                                borderRadius: "12px",
                                fontWeight: "100",
                              },
                            }}
                          />
                        )}
                      </span>
                    }
                    event={handleSubmit}
                    disabled={isSearchLoading}
                    backgroundColor="var(--search-button-background-color)"
                    color="#ffffff"
                    iconWidth="20px"
                    className="search-button-sharp-left"
                    superShine={true}
                    style={{
                      borderTopLeftRadius: "0",
                      borderBottomLeftRadius: "0",
                      borderTopRightRadius: "10px",
                      borderBottomRightRadius: "10px",
                      backgroundColor: "var(--search-button-background-color)",
                      color: "#ffffff",
                      // padding: "10px 10px",
                      height: isExpanded ? "85px" : undefined,
                      opacity: isSearchLoading ? 0.6 : 1,
                      cursor: isSearchLoading ? "not-allowed" : "pointer",
                      padding: "11px 14px"
                    }}
                  />
                  {renderedInlineActions && (
                    <div className="rec-inline-actions">
                      {renderedInlineActions}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {showAttachResume ? (
            <>
              <div className="uploaded-files-container">
                {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 && (
                  <div className="create-uploaded-files-items-list">
                    {uploadedFiles.map((file, index) => (
                      <div className="uploaded-filenames" key={index}>
                        <FileIcon fileName={file.name} />
                        <div className="uploaded-file-container">
                          <div className="uploaded-filename">
                            {displayFileName(file.name)}
                          </div>
                          <div className="uploaded-file-size">
                            {formatFileSize(file.size)}
                          </div>
                        </div>
                        <CancelIcon
                          className="ai-close-icon"
                          onClick={() => onFileDelete(file.name)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* <div
                className="rec-attach-container"
                style={{ marginBottom: "2%", marginTop: "10px" }}
              >
                <label htmlFor="file-upload" className="attach-label">
                  <img
                    alt=""
                    src={candidateResumeIcon}
                    style={{ width: "24px" }}
                  />
                  <div className="attach-label-text">Attach Resume</div>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={onFileUpload}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  multiple
                />
              </div> */}
            </>
          ) : null}
        </>
      )}
      {isChatPopupOpen && (
        <ChatPopup onClose={handleChatPopupClose} isDarkMode={isDarkMode} />
      )}
    </div>
  );
};

export default RecommendedSearchBox;
